import { useState } from 'react';
import { useCookies } from 'react-cookie';
import {
  SetterOrUpdater,
  useRecoilState,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import FOSBSDatePicker from '../../common/FOSBSDatePicker';
import FOSBSMultiSelectAsync from '../../common/FOSBSMultiSelectAsync';
import {
  FormPart,
  GeneralForm,
  fixtureSearchGeneralForm,
  MultiSelectOption,
  MultiSelectOptions,
  vesselSearchResults,
} from '../../state/searchFixture';
import { MainThemeBootstrap } from '../../Theme/MainThemeBootstrap';
import { useEffect, useRef } from 'react';
import FOSBSTextInput from '../../common/FOSBSTextInput';
import FOSBSMultiSelect from '../../common/FOSBSMultiSelect';
import FOSBSMultiSelectSort from '../../common/FOSBSMultiSelectSort';
import { getAPIPath } from '../../core';
import FOSBSBoolean from '../../common/FOSBSBoolean';
import FOSBSTriggerSelect from '../../common/FOSBSTriggerSelect';
import { convertToFixtureSearchPayload } from '../../mapper/fixture-search';
import { SearchResults } from '../SearchResults';
import { Collapse } from 'react-collapse';
import FOSBSRange from '../../common/FOSBSRange';
import { SearchColumns, createSafeColumnsHandler, loadColumnsPreference } from '../search-columns';
import {
  searchFixtures,
  updateSearchColumns,
  getSearchColumns,
} from '../../services/fosService';
import { fixtureGlobalIdLookupState } from '../../state/openFixture';
import { modalEditorOpen } from '../../state/modals';
import { activeModalType, ModalType } from '../../state/modals';
import { userState } from '../../state/state';
import BarLoader from 'react-spinners/BarLoader';
import { createOptionsFromEnum, union } from '../../util';
import { RequirementTypes } from '../../Modals/CreateFixtureModal';

const allColumns = SearchColumns()
  .Fixture.map((el) => el.accessor)
  .filter((el) => el);
const defaultColumns = SearchColumns()
  .Fixture.filter((el) => el.default)
  .map((el) => el.accessor)
  .filter((el) => el);

const mapToMultiSelect = (rawArr: any[]): MultiSelectOptions => {
  return rawArr.map((el) => {
    return { label: el, value: el };
  });
};

const NewSearch = ({
  cookieKey,
  isBudgetIndication = false,
}: {
  cookieKey: string;
  isBudgetIndication?: boolean;
}) => {
  const [cookies, setCookie] = useCookies([cookieKey]);

  // user params
  const [userStateVal, setUserStateVal] = useRecoilState(userState);

  // fixture params
  const setFixtureGlobalId = useSetRecoilState(fixtureGlobalIdLookupState);
  const [moodalOpen, setModalOpen] = useRecoilState(modalEditorOpen);
  const setModalType = useSetRecoilState(activeModalType);

  // paging params
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortBy, setSortBy] = useState({ id: 'vessel_name', desc: false });

  // search params
  const [combined, setCombined] = useState(false);
  const [searched, setSearched] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [removedRows, setRemovedRows] = useState<string[]>([]);
  const [searchRequests, setSearchRequests] = useState<any[]>([{}]);

  // data lists
  const [fixturePossibleColumns, setFixturePossibleColumns] =
    useState<MultiSelectOptions>([]);
  const [definedColumns, setDefinedColumns] = useState<MultiSelectOptions>([]);
  const [vesselTypes, setVesselTypes] = useState<MultiSelectOptions>([]);
  const [fixtureCurrencies, setFixtureCurrencies] =
    useState<MultiSelectOptions>([]);
  const [fixtureRegions, setFixtureRegions] = useState<MultiSelectOptions>([]);
  const [fixtureBrokers, setFixtureBrokers] = useState<MultiSelectOptions>([]);

  // form states
  const [generalFormState, setGeneralFormState] = useRecoilState(
    fixtureSearchGeneralForm
  );

  // search result states
  const [searchResults, setSearchResults] = useRecoilState(vesselSearchResults);

  // form resets
  const resetGeneral = useResetRecoilState(fixtureSearchGeneralForm);

  // form state refs
  const latestGeneralFormState = useRef(generalFormState);
  // On mount!
  useEffect(() => {
    const loadData = async () => {
      const res = await fetch(getAPIPath() + `/api/meta/vessels/general`, {
        method: 'GET', // *GET, POST, PUT, DELETE, etc.
        credentials: 'include', // include, *same-origin, omit
        headers: {},
        redirect: 'follow', // manual, *follow, error
        referrerPolicy: 'no-referrer', // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      });

      if (res.status !== 200) {
        // TODO: Handle load error
      }

      const generalMetaInfo = await res.json();

      setFixturePossibleColumns(mapToMultiSelect(allColumns));
      setVesselTypes(mapToMultiSelect(generalMetaInfo.vesselTypes));
      setFixtureCurrencies(mapToMultiSelect(generalMetaInfo.fixtureCurrencies));
      setFixtureRegions(mapToMultiSelect(generalMetaInfo.fixtureRegions));
      setFixtureBrokers(mapToMultiSelect(generalMetaInfo.fixtureBrokers));

      // Use the safe columns loading function
      await loadColumnsPreference(
        userStateVal,
        cookieKey,
        cookies,
        setDefinedColumns,
        setCookie,
        defaultColumns,
        mapToMultiSelect,
        getSearchColumns
      );
    };

    loadData();
  }, []);

  // subscribe for state updates (needed due to use of memoized components)
  useEffect(
    () => {
      latestGeneralFormState.current = generalFormState;
    },
    // deps to watch for changes in
    [generalFormState]
  );

  useEffect(() => {
    if (searched) {
      doSearch();
    }
  }, [page, perPage, sortBy]);

  // refresh table each time user removes row
  useEffect(() => {
    if (removedRows?.length) {
      doSearch();
    }
  }, [removedRows]);

  const filteredColumns = definedColumns.map((accessor: MultiSelectOption) =>
    SearchColumns().Fixture.find((column) => column.accessor === accessor.value)
  );

  const getPayload = () => {
    const displayFields = definedColumns.map((columns) => columns.value);

    return {
      requests: searchRequests,
      displayFields,
      removedRows,
    };
  };

  const getCurrentPayload = () => {
    // Convert to payolad
    const payload = convertToFixtureSearchPayload({
      general: { ...generalFormState, isBudgetIndication: isBudgetIndication },
    });

    console.log('Payload:', payload);

    return payload;
  };

  const openFixture = (fixtureGlobalId: string) => {
    setFixtureGlobalId(fixtureGlobalId);
    setModalOpen(true);
    setModalType(
      isBudgetIndication
        ? ModalType.UpdateBudgetIndication
        : ModalType.OpenFixture
    );
  };

  const removeRows = (rows: string[]) => {
    if (rows) {
      setRemovedRows(union(removedRows || [], rows));
    }
  };

  const doSearch = async () => {
    setLoading(true);

    const finalRequestBody = getCurrentPayload();

    searchRequests[searchRequests.length - 1] = finalRequestBody;
    setSearchRequests(searchRequests);

    const displayFields = definedColumns.map((columns) => columns.value);
    const searchResponse = await searchFixtures(
      searchRequests,
      displayFields,
      removedRows,
      page,
      perPage,
      sortBy
    );

    const fixturesMapped = searchResponse.fixtures.map((el: any) => {
      return {
        id: `${el.global_id}`,
        ...el,
      };
    });

    const newSearchResultsFiltered = fixturesMapped.filter(
      (el: any, index: any) => {
        return (
          fixturesMapped.findIndex((matchEl: any) => {
            return matchEl.global_id === el.global_id; // match on global id
          }) === index
        );
      }
    );

    setLoading(false);
    setSearchResults(newSearchResultsFiltered);
    setTotalRecords(searchResponse.pagination.total);
    setSearched(true);
    setTotalPages(searchResponse.pagination.lastPage);

    await updateSearchColumns(
      userStateVal.userId,
      cookieKey,
      definedColumns.map((columns) => columns.value)
    );
  };

  const doReset = () => {
    resetGeneral();

    setRemovedRows([]);
    setSearchRequests([{}]);
  };

  const setFormStateElement = (
    formPart: FormPart,
    field: string,
    value: any
  ) => {
    // fetch setter method and current (latest) version of the data.
    const getPrevValueAndSetter = (
      fp: FormPart
    ): {
      existingValue: GeneralForm; // | PositionsForm | ExtrasForm | UnderDeckForm | OtherForm,
      setter: SetterOrUpdater<any>;
    } => {
      switch (fp) {
        default:
        case FormPart.General:
          return {
            existingValue: latestGeneralFormState.current,
            setter: setGeneralFormState,
          };
      }
    };

    // fetch prev val and setter based on existing values
    const { existingValue, setter } = getPrevValueAndSetter(formPart);

    // call setter func and update its vals
    setter({
      ...existingValue,
      [field]: value,
    });
  };

  return (
    <MainThemeBootstrap>
      <div className='container-fluid'>
        <p className='pt-3'>
          <a
            className='btn btn-danger btn-sm'
            href='#collapseExample'
            role='button'
            onClick={() => {
              setCollapsed(!collapsed);
            }}
          >
            Toggle search
          </a>
          <a
            style={{ marginLeft: '20px' }}
            className='btn btn-outline-primary btn-sm'
            href='#collapseExample'
            role='button'
            onClick={() => {
              setSearchRequests([...searchRequests, {}]);
              setCollapsed(false);
              setCombined(true);
            }}
          >
            Add new search
          </a>
          {combined && (
            <a
              style={{ marginLeft: '20px' }}
              className='btn btn-outline-danger btn-sm'
              href='#'
              role='button'
              onClick={() => {
                setSearchRequests([{}]);
                setCombined(false);
                doReset();
              }}
            >
              Reset
            </a>
          )}
        </p>

        <Collapse isOpened={!collapsed}>
          <div className='row'>
            <div className='col-md-6'>
              <div className='col-md-12'>
                <h5 className='fw-light pt-3 border-bottom border-5 border-secondary pb-1'>
                  {isBudgetIndication
                    ? 'Budget Indication Info'
                    : 'Fixture Info'}
                </h5>
              </div>
              <FOSBSTriggerSelect
                displayVertical={false}
                label={'Requirement Type'}
                options={createOptionsFromEnum(RequirementTypes)}
                name={'type'}
                value={generalFormState.type} //fixtureFormState.requirementType ??
                stateSetter={(name: string, value: string) => {
                  setFormStateElement(FormPart.General, name, value);
                }}
              />
              <div className='row pb-1'>
                <label
                  htmlFor='inputEmail3'
                  className='col-sm-4 col-form-label'
                >
                  Delivery Port
                </label>
                <div className='col'>
                  <FOSBSMultiSelectAsync
                    dbTableName='fixtures'
                    dbFieldName='delivery_port'
                    name='deliveryPort'
                    stateSetter={(name: string, value: string) => {
                      setFormStateElement(FormPart.General, name, value);
                    }}
                    value={generalFormState.deliveryPort}
                  />
                </div>
              </div>
              {/* <FOSBSTextInput label={'Delivery Port'} value={generalFormState.deliveryPort} onChange={(e) => { setFormStateElement(FormPart.General, 'deliveryPort', e.target.value) }} /> */}
              <div className='row'>
                <label
                  htmlFor='inputEmail3'
                  className='col-sm-4 col-form-label'
                >
                  Region
                </label>
                <div className='col'>
                  <FOSBSMultiSelect
                    name='region'
                    value={generalFormState.region}
                    options={fixtureRegions}
                    stateSetter={(name: string, value: string) => {
                      setFormStateElement(FormPart.General, name, value);
                    }}
                  />
                </div>
              </div>
              <FOSBSRange
                label='Rate'
                name='rate'
                stateSetter={(name: string, value: string) => {
                  setFormStateElement(FormPart.General, name, value);
                }}
                value={generalFormState.rate}
              />
              <div className='row'>
                <label
                  htmlFor='inputEmail3'
                  className='col-sm-4 col-form-label'
                >
                  Rate Currency
                </label>
                <div className='col'>
                  <FOSBSMultiSelect
                    name='currency'
                    value={generalFormState.currency}
                    options={fixtureCurrencies}
                    stateSetter={(name: string, value: string) => {
                      setFormStateElement(FormPart.General, name, value);
                    }}
                  />
                </div>
              </div>
              <div className='row align-items-center'>
                <label className='col-sm-4 col-form-label'>Fixture Date</label>
                <div className='col'>
                  <FOSBSDatePicker
                    placeholder={'From'}
                    value={generalFormState.fixtureDateFrom}
                    stateSetter={(name, value) => {
                      setFormStateElement(FormPart.General, name, value);
                    }}
                    name={'fixtureDateFrom'}
                  />
                </div>
                <div className='col'>
                  <FOSBSDatePicker
                    placeholder={'To'}
                    value={generalFormState.fixtureDateTo}
                    stateSetter={(name, value) => {
                      setFormStateElement(FormPart.General, name, value);
                    }}
                    name={'fixtureDateTo'}
                  />
                </div>
              </div>
              <div className='row pb-1'>
                <label
                  htmlFor='inputEmail3'
                  className='col-sm-4 col-form-label'
                >
                  Charterer
                </label>
                <div className='col'>
                  <FOSBSMultiSelectAsync
                    dbTableName='fixtures'
                    dbFieldName='charterer'
                    name='charterer'
                    stateSetter={(name: string, value: string) => {
                      setFormStateElement(FormPart.General, name, value);
                    }}
                    value={generalFormState.charterer}
                  />
                </div>
              </div>
              <FOSBSBoolean
                label={'Own'}
                name={'own'}
                value={generalFormState.own}
                stateSetter={(name: string, value: string) => {
                  setFormStateElement(FormPart.General, name, value);
                }}
              />
              <div className='row'>
                <label
                  htmlFor='inputEmail3'
                  className='col-sm-4 col-form-label'
                >
                  Broker
                </label>
                <div className='col'>
                  <FOSBSMultiSelect
                    name='broker'
                    value={generalFormState.broker}
                    options={fixtureBrokers}
                    stateSetter={(name: string, value: string) => {
                      setFormStateElement(FormPart.General, name, value);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className='row'>
            <div className='col-sm-12 col-md-6 col-lg-6 bg-light pt-2'>
              <div className='col-md-12'>
                <h5 className='fw-light pt-3 border-bottom border-5 border-secondary pb-1'>
                  General Info
                </h5>
              </div>

              <div className='row'>
                <label
                  htmlFor='fixtureSearchColumns'
                  className='col-sm-4 col-form-label'
                >
                  Displayed Columns
                </label>
                <div className='col'>
                  <FOSBSMultiSelectSort
                    name='fixtureSearchColumns'
                    value={definedColumns}
                    options={fixturePossibleColumns}
                    stateSetter={createSafeColumnsHandler(
                      setDefinedColumns,
                      setCookie,
                      cookieKey,
                      userStateVal.userId,
                      defaultColumns
                    )}
                  />
                </div>
              </div>

              <FOSBSTextInput
                label={'Imo Number'}
                value={generalFormState.imoNumber}
                onChange={(e) => {
                  setFormStateElement(
                    FormPart.General,
                    'imoNumber',
                    e.target.value
                  );
                }}
              />
              <FOSBSTextInput
                label={'Vessel Name'}
                value={generalFormState.vesselName}
                onChange={(e) => {
                  setFormStateElement(
                    FormPart.General,
                    'vesselName',
                    e.target.value
                  );
                }}
              />
              <FOSBSTextInput
                label={'Owner'}
                value={generalFormState.owner}
                onChange={(e) => {
                  setFormStateElement(
                    FormPart.General,
                    'owner',
                    e.target.value
                  );
                }}
              />

              <div className='row'>
                <label
                  htmlFor='inputEmail3'
                  className='col-sm-4 col-form-label'
                >
                  Vessel Type
                </label>
                <div className='col'>
                  <FOSBSMultiSelect
                    name='vesselSubType'
                    value={generalFormState.vesselSubType}
                    options={vesselTypes}
                    stateSetter={(name: string, value: string) => {
                      setFormStateElement(FormPart.General, name, value);
                    }}
                  />
                </div>
              </div>

              <FOSBSTriggerSelect
                displayVertical={false}
                label={'Status'}
                options={[
                  { label: 'Active', value: 'Active' },
                  { label: 'Old', value: 'Old' },
                ]}
                name={'status'}
                value={generalFormState.status} //fixtureFormState.requirementType ??
                stateSetter={(name: string, value: string) => {
                  setFormStateElement(FormPart.General, name, value);
                }}
              />
              <div className='row align-items-center'>
                <label className='col-sm-4 col-form-label'>Built</label>
                <div className='col'>
                  <FOSBSDatePicker
                    placeholder={'From'}
                    value={generalFormState.builtFrom}
                    stateSetter={(name, value) => {
                      setFormStateElement(FormPart.General, name, value);
                    }}
                    name={'builtFrom'}
                  />
                </div>
                <div className='col'>
                  <FOSBSDatePicker
                    placeholder={'To'}
                    value={generalFormState.builtTo}
                    stateSetter={(name, value) => {
                      setFormStateElement(FormPart.General, name, value);
                    }}
                    name={'builtTo'}
                  />
                </div>
              </div>
              <FOSBSRange
                label='DWT'
                name='DWT'
                stateSetter={(name: string, value: string) => {
                  setFormStateElement(FormPart.General, name, value);
                }}
                value={generalFormState.DWT}
              />
            </div>
          </div>

          <div className='row'>
            <div
              className='col-md-12'
              style={{ textAlign: 'center', marginTop: 10, marginBottom: 10 }}
            >
              <button
                className='btn btn-success'
                style={{ marginRight: 5 }}
                onClick={() => {
                  doSearch();
                  setCollapsed(true);
                }}
              >
                Search
              </button>
              <button
                className='btn btn-secondary'
                style={{ marginRight: 5 }}
                onClick={doReset}
              >
                Reset
              </button>
            </div>
          </div>
        </Collapse>
      </div>

      <div className='container-fluid'>
        <div className='row pt-3 ' style={{ minHeight: '400px' }}>
          <div className='col-sm-12 col-md-12 col-lg-12'>
            <BarLoader
              loading={loading}
              height={4}
              width={200}
              cssOverride={{ display: 'block', margin: 'auto' }}
            />

            {searched && !loading && (
              <span className='btn btn-light mb-3'>
                Matches{' '}
                <span className='badge' style={{ backgroundColor: '#2B5B5F' }}>
                  {totalRecords}
                </span>
              </span>
            )}

            {searched && !loading && selectedRowIds.length !== 0 && (
              <a
                style={{ marginBottom: '15px', marginLeft: '10px' }}
                className='btn btn-outline-danger btn-sm'
                href='#'
                role='button'
                onClick={() => {
                  removeRows(selectedRowIds);
                }}
              >
                Remove rows
              </a>
            )}

            {searched && !loading && (
              <SearchResults
                editable={true}
                openEditor={openFixture}
                data={searchResults}
                columns={filteredColumns}
                onRowSelectStateChange={setSelectedRowIds}
                setPage={setPage}
                setPerPage={setPerPage}
                page={page}
                perPage={perPage}
                totalPages={totalPages}
                sortBy={sortBy}
                setSortBy={setSortBy}
                onChangeSort={setSortBy}
                fileName={
                  isBudgetIndication
                    ? 'search-budget-indication'
                    : 'search-fixtures'
                }
                exportTable={
                  isBudgetIndication ? 'budget_indication' : 'fixture'
                }
                exportRowKey={'global_id'}
                getPayload={getPayload}
              />
            )}
          </div>
        </div>
      </div>
    </MainThemeBootstrap>
  );
};

export default NewSearch;
